/**
 * Shared `<img onError>` and silent-portrait-fallthrough warner.
 *
 * Browser image-fetch failures (401/403/404/CORS) don't surface to
 * the JavaScript console by default — they appear only in the
 * DevTools Network tab with the "Img" filter on. Two stacked silent
 * patterns swallowed the BOU-771 cutscene + portrait regression in
 * prod (see BOU-778):
 *
 *   1. Backend coerced None → "" for portrait fields, so frontends
 *      received empty strings and treated them as "no portrait" instead
 *      of surfacing a real load failure.
 *   2. Components rendered <img> without onError, so 4xx responses
 *      from the proxy never reached our auth-wrapper warnings.
 *
 * Use `logImageFailure(src, alt, context)` on every <img onError>
 * we render; use `warnUnresolvablePortrait` from
 * QuestConversationPortrait (and similar) when the data shape says a
 * portrait *should* exist but the URL pipeline yielded null.
 *
 * Both warnings are gated behind `import.meta.env.DEV` so prod
 * consoles stay quiet — the underlying useAuthorizedMediaUrl /
 * Cloud CDN proxy already deduplicate cached failures, so we only
 * want signal for active developers debugging.
 */

// Vite-style DEV gate. `import.meta.env.DEV` is a build-time literal in
// Vite (and the test runner is configured the same way), so this is
// statically evaluable and dead-code-eliminated in prod bundles.
const DEV = Boolean(import.meta?.env?.DEV);

/**
 * Log a single failed <img> response. Pair with onError on every
 * <img> we render under our control. Cheap to call — gated on DEV.
 *
 * @param {string|null|undefined} src - The src attribute that failed
 * @param {string|null|undefined} alt - The alt attribute (helps identify the image)
 * @param {string} context - Short tag for which component/site emitted it
 */
export function logImageFailure(src, alt, context) {
  if (!DEV) return;
  console.warn('[Image] failed to load', { context, src, alt });
}

/**
 * Helper for components that render an initial-letter / placeholder
 * fallback when the URL pipeline returned null *despite* the data
 * carrying enough identity to look one up. The empty-state is
 * legitimate when the underlying record genuinely has no portrait;
 * surfacing the *unresolvable* case lets us catch silent regressions
 * where the resolver / context is dropping a URL it should have
 * returned. Gated on DEV.
 *
 * @param {object} identity - { context, characterId, name }
 */
export function warnUnresolvablePortrait(identity) {
  if (!DEV) return;
  if (!identity) return;
  const { context = 'unknown', characterId, name } = identity;
  if (!characterId && !name) return;
  console.warn(
    '[QuestConversationPortrait] no resolvable portrait for',
    { context, characterId, name },
  );
}
